import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Clock, ChevronLeft, ChevronRight, BookOpen, CheckCircle2 } from "lucide-react";
import { API } from "../App";

const TutorialDetail = () => {
  const { level, tutorialId } = useParams();
  const navigate = useNavigate();
  const [tutorial, setTutorial] = useState(null);
  const [allTutorials, setAllTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const fetchTutorial = async () => {
      try {
        const [tutorialRes, allRes] = await Promise.all([
          axios.get(`${API}/tutorials/${level}/${tutorialId}`),
          axios.get(`${API}/tutorials/${level}`)
        ]);
        setTutorial(tutorialRes.data);
        setAllTutorials(allRes.data.tutorials);
        
        const index = allRes.data.tutorials.findIndex(t => t.id === tutorialId);
        setCurrentIndex(index);
      } catch (error) {
        console.error("Error fetching tutorial:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorial();
  }, [level, tutorialId]);
  
  const goToTutorial = (direction) => {
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= 0 && newIndex < allTutorials.length) {
      navigate(`/learn/${level}/${allTutorials[newIndex].id}`);
    }
  };
  
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    // Simple markdown parsing
    const lines = content.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let inTable = false;
    let tableRows = [];
    
    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(<pre key={index}><code>{codeContent.join('\n')}</code></pre>);
          codeContent = [];
        }
        inCodeBlock = !inCodeBlock;
        return;
      }
      
      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }
      
      // Tables
      if (line.startsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
        if (!cells.every(c => c.match(/^-+$/))) {
          tableRows.push(cells);
        }
        return;
      } else if (inTable) {
        const header = tableRows[0];
        const body = tableRows.slice(1);
        elements.push(
          <table key={index} className="tutorial-table">
            <thead>
              <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>{row.map((c, ci) => <td key={ci}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        );
        inTable = false;
        tableRows = [];
      }
      
      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h3 key={index}>{line.slice(4)}</h3>);
      } else if (line.startsWith('## ')) {
        elements.push(<h2 key={index}>{line.slice(3)}</h2>);
      } else if (line.startsWith('# ')) {
        elements.push(<h1 key={index}>{line.slice(2)}</h1>);
      }
      // Lists
      else if (line.startsWith('- ')) {
        elements.push(<li key={index}>{formatInline(line.slice(2))}</li>);
      }
      else if (line.match(/^\d+\. /)) {
        elements.push(<li key={index}>{formatInline(line.replace(/^\d+\. /, ''))}</li>);
      }
      // Paragraphs
      else if (line.trim()) {
        elements.push(<p key={index}>{formatInline(line)}</p>);
      }
    });
    
    return elements;
  };
  
  const formatInline = (text) => {
    // Bold
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Code
    text = text.replace(/`(.+?)`/g, '<code>$1</code>');
    
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };
  
  if (loading) {
    return (
      <div className="tutorial-loading">
        <div className="spinner" />
        <p>Loading tutorial...</p>
      </div>
    );
  }
  
  if (!tutorial) {
    return (
      <div className="tutorial-not-found">
        <h2>Tutorial not found</h2>
        <Link to="/learn" className="btn btn-primary">Back to Learning Hub</Link>
      </div>
    );
  }
  
  return (
    <div className="tutorial-detail" data-testid="tutorial-detail">
      <div className="container">
        {/* Navigation */}
        <div className="tutorial-nav" data-testid="tutorial-nav">
          <Link to="/learn" className="back-link" data-testid="back-to-hub">
            <ArrowLeft size={18} />
            Back to Learning Hub
          </Link>
          <div className="progress-indicator">
            <span>{currentIndex + 1} of {allTutorials.length}</span>
          </div>
        </div>
        
        {/* Header */}
        <motion.div 
          className="tutorial-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className={`level-badge ${level}`}>{level}</span>
          <h1>{tutorial.title}</h1>
          <p className="tutorial-description">{tutorial.description}</p>
          <div className="tutorial-meta">
            <span><Clock size={16} /> {tutorial.duration}</span>
            <span><BookOpen size={16} /> Lesson {currentIndex + 1}</span>
          </div>
        </motion.div>
        
        {/* Diagram */}
        {tutorial.diagram_url && (
          <motion.div
            className="tutorial-diagram"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
          >
            <img src={tutorial.diagram_url} alt={`${tutorial.title} - Visual Guide`} data-testid="tutorial-diagram" />
          </motion.div>
        )}
        
        {/* Content */}
        <motion.div 
          className="tutorial-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          data-testid="tutorial-content"
        >
          {renderMarkdown(tutorial.content)}
        </motion.div>
        
        {/* Navigation */}
        <div className="tutorial-footer" data-testid="tutorial-footer">
          <button 
            className="nav-btn prev"
            onClick={() => goToTutorial('prev')}
            disabled={currentIndex === 0}
            data-testid="prev-tutorial-btn"
          >
            <ChevronLeft size={20} />
            <span>Previous Lesson</span>
          </button>
          
          <div className="completion-status">
            <CheckCircle2 size={20} />
            <span>Lesson Complete!</span>
          </div>
          
          <button 
            className="nav-btn next"
            onClick={() => goToTutorial('next')}
            disabled={currentIndex === allTutorials.length - 1}
            data-testid="next-tutorial-btn"
          >
            <span>Next Lesson</span>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      <style>{`
        .tutorial-detail {
          padding: 40px 0 80px;
        }
        
        .tutorial-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
        }
        
        .back-link {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: var(--text-primary);
        }
        
        .progress-indicator {
          font-size: 14px;
          color: var(--text-muted);
        }
        
        .tutorial-header {
          margin-bottom: 48px;
        }
        
        .tutorial-header h1 {
          font-size: 2.5rem;
          margin: 16px 0;
        }
        
        .tutorial-description {
          font-size: 1.125rem;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }
        
        .tutorial-meta {
          display: flex;
          gap: 24px;
        }
        
        .tutorial-meta span {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-muted);
        }
        
        .tutorial-diagram {
          margin-bottom: 32px;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid var(--border);
          background: var(--bg-paper);
        }
        .tutorial-diagram img {
          width: 100%;
          display: block;
        }
        
        .tutorial-content {
          background: var(--bg-paper);
          border-radius: 16px;
          padding: 48px;
          margin-bottom: 32px;
        }
        
        .tutorial-content h1 { font-size: 2rem; margin-top: 0; }
        .tutorial-content h2 { font-size: 1.5rem; color: var(--primary-light); margin-top: 2.5rem; }
        .tutorial-content h3 { font-size: 1.25rem; margin-top: 2rem; }
        
        .tutorial-content p {
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }
        
        .tutorial-content ul,
        .tutorial-content ol {
          margin-bottom: 1.5rem;
          padding-left: 0;
          list-style: none;
        }
        
        .tutorial-content li {
          color: var(--text-secondary);
          margin-bottom: 8px;
          padding-left: 24px;
          position: relative;
        }
        
        .tutorial-content li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 10px;
          width: 6px;
          height: 6px;
          background: var(--primary);
          border-radius: 50%;
        }
        
        .tutorial-content strong {
          color: var(--text-primary);
        }
        
        .tutorial-content code {
          font-family: var(--font-mono);
          background: var(--bg-subtle);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .tutorial-content pre {
          background: var(--bg-subtle);
          padding: 20px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1.5rem 0;
          border: 1px solid var(--border);
        }
        
        .tutorial-content pre code {
          background: none;
          padding: 0;
        }
        
        .tutorial-table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        
        .tutorial-table th,
        .tutorial-table td {
          padding: 12px 16px;
          text-align: left;
          border: 1px solid var(--border);
        }
        
        .tutorial-table th {
          background: var(--bg-subtle);
          font-weight: 600;
        }
        
        .tutorial-table td {
          font-family: var(--font-mono);
          font-size: 14px;
        }
        
        .tutorial-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          background: var(--bg-paper);
          border-radius: 12px;
        }
        
        .nav-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s, border-color 0.2s;
        }
        
        .nav-btn:hover:not(:disabled) {
          background: var(--bg-default);
          border-color: var(--border-active);
        }
        
        .nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .completion-status {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--success);
          font-weight: 500;
        }
        
        .tutorial-loading,
        .tutorial-not-found {
          text-align: center;
          padding: 100px 20px;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .tutorial-header h1 {
            font-size: 1.75rem;
          }
          
          .tutorial-content {
            padding: 24px;
          }
          
          .tutorial-footer {
            flex-direction: column;
            gap: 16px;
          }
          
          .nav-btn {
            width: 100%;
            justify-content: center;
          }
          
          .completion-status {
            order: -1;
          }
        }
      `}</style>
    </div>
  );
};

export default TutorialDetail;